/**
 * ConfirmDialog — Reusable in-app confirmation dialog.
 *
 * Replaces browser-native window.confirm() with a Radix AlertDialog.
 * Use the `variant` prop to signal destructive vs neutral actions.
 *
 * @param open          - Controlled open state
 * @param onOpenChange  - Called when dialog requests close
 * @param title         - Dialog heading
 * @param description   - Body text explaining the action
 * @param confirmLabel  - Confirm button label (default: "Confirm")
 * @param cancelLabel   - Cancel button label (default: "Cancel")
 * @param variant       - "destructive" (red) | "default" (blue)
 * @param onConfirm     - Called when user presses the confirm button
 */
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/AlertDialog';

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'destructive' | 'default';
  onConfirm: () => void;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'destructive',
  onConfirm,
}: ConfirmDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="bg-zinc-800 border-zinc-700 text-white">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-white">{title}</AlertDialogTitle>
          <AlertDialogDescription className="text-zinc-400">{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="bg-zinc-700 border-zinc-600 text-white hover:bg-zinc-600 hover:text-white">
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className={
              variant === 'destructive'
                ? 'bg-red-600 hover:bg-red-700 text-white border-0'
                : 'bg-blue-600 hover:bg-blue-700 text-white border-0'
            }
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
